/**
 * The app sidebar — the journal's, with this product's pages in it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT COLLAPSES TO A RAIL, WHICH IS WHAT THE OLD ONE COULD NOT DO
 *
 * `SideRail` was a fixed 60px strip of icons with a hover tooltip. It never
 * expanded, so the labels were never readable — you learned the icons or you
 * hovered. This is `collapsible="icon"`: expanded it is 16rem with names,
 * collapsed it is the same rail as before, and the state persists.
 *
 * The nav ITEMS are unchanged — same six screens, same order, same icons,
 * same `data-testid`s — because muscle memory for a trading terminal is
 * worth more than a tidy menu, and the order was already the mockups'.
 *
 * WHAT IS STILL NOT HERE: Accounts and Strategies. Both are set up between
 * sessions rather than reached during one, so they stay in the user menu at
 * the foot — see `NavUser`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE COLLAPSED HEADER IS A LAUNCHER, NOT A LOGO
 *
 * Collapsed, the logo square becomes the expand button and swaps to a panel
 * icon on hover. It is the only control at that width, and a logo that does
 * nothing in a 56px rail wastes the one slot that is always visible.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useNavigate } from 'react-router-dom'
import {
  Briefcase,
  LayoutDashboard,
  NotebookPen,
  PanelRight,
} from 'lucide-react'
// GraduationCap, PenSquare and Timer come back with Schedule, Drafts and
// Learn below.
import { LogoMark } from '@/components/Logo'
import { NavUser } from '@/components/NavUser'
import { NavMain, type NavItem } from '@/components/NavMain'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'

/**
 * The six screens a trading day moves between.
 *
 * Phase 1 ships Terminal and Portfolio as real routes; the rest render a
 * "later phase" placeholder rather than being hidden, so the shape of the
 * product stays visible while it is built. A nav item that appears later
 * moves the ones around it.
 */
const NAV: readonly NavItem[] = [
  {
    title: 'Terminal',
    url: '/',
    icon: LayoutDashboard,
    testid: 'nav-terminal',
  },
  {
    title: 'Journal',
    url: '/journal',
    icon: NotebookPen,
    testid: 'nav-journal',
  },
  {
    title: 'Portfolio',
    url: '/portfolio',
    icon: Briefcase,
    testid: 'nav-portfolio',
  },

  // { title: 'Schedule', url: '/schedule', icon: Timer, testid: 'nav-schedule' },
  // { title: 'Drafts', url: '/drafts', icon: PenSquare, testid: 'nav-drafts' },
  // { title: 'Learn', url: '/learn', icon: GraduationCap, testid: 'nav-learn' },
]

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const navigate = useNavigate()
  const { toggleSidebar } = useSidebar()

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="border-b border-sidebar-border px-2 py-2">
        {/* Expanded, on a desktop. */}
        <div className="hidden items-center gap-2 group-data-[collapsible=icon]:hidden lg:flex">
          <button
            type="button"
            onClick={() => navigate('/')}
            data-testid="rail-logo"
            aria-label="ApnaAlgo home"
            className={cn(
              'flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2',
              'transition-colors hover:bg-sidebar-accent/50',
            )}
          >
            <LogoMark size={28} className="shrink-0" />
            <span className="block truncate text-[15px] font-semibold">
              ApnaAlgo
            </span>
          </button>

          <SidebarTrigger className="h-9 w-9 shrink-0 rounded-lg" />
        </div>

        {/* Collapsed: the logo IS the expand button. */}
        <div className="hidden justify-center group-data-[collapsible=icon]:lg:flex">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Open sidebar"
            data-testid="rail-logo-collapsed"
            className={cn(
              'group relative flex h-11 w-11 items-center justify-center rounded-xl',
              'transition-colors duration-200 hover:bg-sidebar-accent',
            )}
          >
            <span className="absolute inset-0 flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0">
              <LogoMark size={26} />
            </span>
            <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <PanelRight className="h-[18px] w-[18px]" />
            </span>
          </button>
        </div>

        {/* On a phone the sidebar is a sheet, so it is always "expanded". */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            onClick={() => navigate('/')}
            aria-label="ApnaAlgo home"
            className="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2"
          >
            <LogoMark size={28} className="shrink-0" />
            <span className="block truncate text-[15px] font-semibold">
              ApnaAlgo
            </span>
          </button>
          <SidebarTrigger className="h-9 w-9 shrink-0 rounded-lg" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={NAV} />
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>

      {/* The draggable edge. Clicking it toggles, which is the affordance
          people reach for before they find the header button. */}
      <SidebarRail />
    </Sidebar>
  )
}
